import { useEffect, useState } from 'react'
import { Link, NavLink } from 'react-router'
import { Images, ShieldCheck, Upload } from 'lucide-react'

import { Logo } from '@/components/brand/logo'
import { SearchBox } from '@/components/search/search-box'
import { useSearchNavigation } from '@/components/search/use-search-navigation'
import { ThemeToggle } from '@/components/layout/theme-toggle'
import { UserMenu } from '@/components/layout/user-menu'
import { useAuth } from '@/hooks/use-auth'
import { cn } from '@/lib/utils'

const navItems = [
  { to: '/', label: 'Gallery', icon: Images, end: true },
  { to: '/upload', label: 'Upload', icon: Upload, end: false },
] as const

const adminItem = { to: '/admin', label: 'Admin', icon: ShieldCheck, end: false } as const

export function Header() {
  const [scrolled, setScrolled] = useState(false)
  const search = useSearchNavigation()
  const { isAdmin } = useAuth()
  const items = isAdmin ? [...navItems, adminItem] : navItems

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <header className="pt-safe fixed inset-x-0 top-0 z-40">
      <div className="mx-auto max-w-[110rem] px-2 pt-2 sm:px-4 sm:pt-3">
        <div
          className={cn(
            'glass flex h-14 items-center gap-2 rounded-2xl px-2 transition-shadow duration-300 sm:px-3',
            scrolled && 'shadow-soft',
          )}
        >
          <Link to="/" className="shrink-0 rounded-lg px-1.5 py-1" aria-label="Kagami – home">
            <Logo />
          </Link>

          <nav className="ml-2 hidden items-center gap-0.5 lg:flex" aria-label="Main">
            {items.map(({ to, label, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  cn(
                    'rounded-lg px-3.5 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary/10 text-foreground ring-1 ring-inset ring-primary/20'
                      : 'text-muted-foreground hover:text-foreground',
                  )
                }
              >
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden flex-1 justify-center px-2 md:flex">
            <SearchBox
              value={search.draft}
              onChange={search.setDraft}
              onSubmit={search.submit}
              className="w-full max-w-xl"
            />
          </div>
          <div className="flex-1 md:hidden" />

          <div className="flex shrink-0 items-center gap-1">
            <ThemeToggle />
            <UserMenu />
          </div>
        </div>
      </div>
    </header>
  )
}
